import * as Popover from '@radix-ui/react-popover'
import {
  ChangeEvent,
  useEffect,
  useImperativeHandle,
  RefObject,
  useRef,
  useCallback,
  KeyboardEvent,
} from 'react'

import { TKeywordMarker } from '@/entities/map'
import { Slot, usePopover } from '@/shared/lib'
import { Icon, TextField, type InputProps } from '@/shared/ui'

import { getLocationName } from '../../lib/location'
import { History } from '../../model/search-history-store/history'
import { AutoCompletePopup } from '../auto-complete-popup'
import { SearchHistoryPopup } from '../search-history-popup'

export type AutoCompleteInputRef = {
  openAutoCompletePopup: () => void
  closeAutoCompletePopup: () => void
}

type Props = Omit<
  InputProps,
  'onChange' | 'ref' | 'value' | 'onFocus' | 'onBlur'
> & {
  value: string
  inputClassName?: string
  popupAlign?: 'start' | 'end'
  popupAlignOffset?: number
  popupSideOffset?: number
  onChange: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  onKeywordSelect?: (location: TKeywordMarker | null) => void
  onEnterKeyDown?: () => void
  onRecentHistoryClick?: (history: History) => void
  ref?: RefObject<AutoCompleteInputRef>
}

export const AutoCompleteInput = ({
  value,
  popupAlign = 'start',
  popupAlignOffset = 0,
  popupSideOffset = 0,
  onChange,
  onFocus,
  onBlur,
  onKeywordSelect,
  onEnterKeyDown,
  onRecentHistoryClick,
  className,
  inputClassName,
  ref: refProp,
  ...props
}: Props) => {
  const popupRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    isOpen,
    ref: triggerRef,
    open,
    close,
    handleOutsideClick: handlePopoverOutsideClick,
  } = usePopover<HTMLDivElement>()

  const openAutoCompletePopup = () => {
    open()
  }

  useImperativeHandle(refProp, () => ({
    openAutoCompletePopup,
    closeAutoCompletePopup: close,
  }))

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return

    if (event.key === 'Enter') {
      event.preventDefault()
      onEnterKeyDown?.()
    } else if (event.key === 'Escape') {
      close()
    } else if (event.key === 'Tab') {
      onBlur?.()
    }
  }

  const handleKeywordClick = (location: TKeywordMarker) => {
    onChange(getLocationName(location))
    onKeywordSelect?.(location)
    close()
    onBlur?.()
  }

  const handleRecentHistoryClick = (history: History) => {
    onChange(history.name)
    onRecentHistoryClick?.(history)
    close()
    onBlur?.()
  }

  const handlePopupClose = () => {
    close()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
    onKeywordSelect?.(null)

    if (!isOpen) {
      open()
    }
  }

  const handleInputReset = () => {
    onChange('')
    onKeywordSelect?.(null)
  }

  const handleInputFocus = () => {
    open()
    onFocus?.()
  }

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (popupRef.current?.contains(event.target as Node)) {
        return
      }

      if (inputRef.current?.contains(event.target as Node)) {
        return
      }

      onBlur?.()
    },
    [onBlur],
  )

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick)

    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [handleOutsideClick])

  return (
    <Popover.Root open={isOpen}>
      <Popover.Trigger asChild>
        <div ref={triggerRef} className={className}>
          <TextField
            {...props}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onReset={handleInputReset}
            className={inputClassName}
            ref={inputRef}
          >
            <Slot name="left">
              <Icon
                name="search"
                size={24}
                className="text-black"
                strokeWidth={1.5}
              />
            </Slot>
          </TextField>
        </div>
      </Popover.Trigger>
      {(() => {
        if (!isOpen) return null

        if (value.length) {
          return (
            <AutoCompletePopup
              ref={popupRef}
              value={value}
              onKeywordClick={handleKeywordClick}
              onInteractOutside={handlePopoverOutsideClick}
              align={popupAlign}
              alignOffset={popupAlignOffset}
              sideOffset={popupSideOffset}
            />
          )
        }

        return (
          <SearchHistoryPopup
            onItemClick={handleRecentHistoryClick}
            onClose={handlePopupClose}
            onInteractOutside={handlePopoverOutsideClick}
            align={popupAlign}
            alignOffset={popupAlignOffset}
            sideOffset={popupSideOffset}
          />
        )
      })()}
    </Popover.Root>
  )
}
